/**
 * Secure token utilities for encryption, decryption, and management
 * Uses provided encryption key, VUE_APP_JWT_SECRET_KEY environment variable,
 * or falls back to a default key
 */

// Simple encryption function using a consistent key derivation
function encryptToken(token, customKey = null) {
  if (!token) return '';
  
  // A simple encryption algorithm (not for production use)
  // In a real-world scenario, consider using a proper encryption library like CryptoJS
  const encryptionKey = customKey || process.env.VUE_APP_JWT_SECRET_KEY || 'consark-butterfly-secure-key';
  let encrypted = '';
  
  for (let i = 0; i < token.length; i++) {
    const charCode = token.charCodeAt(i) ^ encryptionKey.charCodeAt(i % encryptionKey.length);
    encrypted += String.fromCharCode(charCode);
  }
  
  // Convert to Base64 for safe storage
  return btoa(encrypted);
}

// Decryption function to recover the original token
function decryptToken(encryptedToken, customKey = null) {
  if (!encryptedToken) return '';
  
  try {
    // Decode from Base64
    const decoded = atob(encryptedToken);
    const encryptionKey = customKey || process.env.VUE_APP_JWT_SECRET_KEY || 'consark-butterfly-secure-key';
    let decrypted = '';
    
    for (let i = 0; i < decoded.length; i++) {
      const charCode = decoded.charCodeAt(i) ^ encryptionKey.charCodeAt(i % encryptionKey.length);
      decrypted += String.fromCharCode(charCode);
    }
    
    return decrypted;
  } catch (error) {
    console.error('Error decrypting token:', error);
    return '';
  }
}

// Store token securely
function storeSecureToken(token, encryptionKey = null) {
  if (!token) {
    return;
  }

  const encrypted = encryptToken(token, encryptionKey);
  // DEV NOTE: '__sk_a7f2d9b' is the localStorage key for encrypted JWT token storage (secure_jwt_token)
  // Do not change this key name as it's used for security through obscurity
  const storageKey = '__sk_a7f2d9b';
  localStorage.setItem(storageKey, encrypted);
}

// Retrieve and decrypt token
function getSecureToken(encryptionKey = null) {
  // DEV NOTE: '__sk_a7f2d9b' is the localStorage key for encrypted JWT token storage (secure_jwt_token)
  const storageKey = '__sk_a7f2d9b';
  const encryptedToken = localStorage.getItem(storageKey);
  const token = decryptToken(encryptedToken || '', encryptionKey);
  return token;
}

// Remove token from storage
function removeSecureToken() {
  // DEV NOTE: '__sk_a7f2d9b' is the localStorage key for encrypted JWT token storage (secure_jwt_token)
  const storageKey = '__sk_a7f2d9b';
  localStorage.removeItem(storageKey);
}

export default {
  encryptToken,
  decryptToken,
  storeSecureToken,
  getSecureToken,
  removeSecureToken
};
